import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusCircle,
  ChevronRight,
  ChevronLeft,
  Trash2,
  DollarSign,
  GraduationCap,
  MessageSquare,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import AdComponent from './components/AdComponent';
import { Message, Chat } from './types';
import { toast } from 'react-hot-toast';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import About from './components/About';
import Notifications from './components/Notifications';
import BankInformation from './components/BankInformation';
import Sidebar from './components/Sidebar';
import ProfileSettings from './components/ProfileSettings';
import ProfileIcon from './components/ProfileIcon';
import ChatComponent from './components/Chat';
import PWAFooter from './components/PWAFooter';
import ModeSelector from './components/ModeSelector';
import { useAuth } from './contexts/AuthContext';
import { checkConnection } from './lib/supabase';
import { rlService } from './services/rlService';
import LearningProgress from './components/LearningProgress';
import UpgradePremium from './components/UpgradePremium';

const DirectCareMode = lazy(() => import('./components/directcare/DirectCareMode'));

export default function App() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [mode, setMode] = useState<'default' | 'finance' | 'direct-care'>('default');
  const [dbConnected, setDbConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLearningOpen, setIsLearningOpen] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(() => false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatIndex, setCurrentChatIndex] = useState<number | null>(null);

  useEffect(() => {
    async function initializeServices() {
      setIsConnecting(true);
      const isConnected = await checkConnection();
      setDbConnected(isConnected);
      if (isConnected) {
        toast.success('Database connected successfully');
        await rlService.syncLocalData();
      } else {
        toast.error('Database connection failed', {
          duration: 5000,
          action: {
            label: 'Retry',
            onClick: () => initializeServices(),
          },
        });
      }
      setIsConnecting(false);
    }
    initializeServices();
  }, []);

  const handleModeChange = (newMode: 'default' | 'finance' | 'direct-care') => {
    const oldModeState = {
      messages,
      chats,
      currentChatIndex,
      hasStartedChat
    };
    localStorage.setItem(`mode_state_${mode}`, JSON.stringify(oldModeState));

    const savedState = localStorage.getItem(`mode_state_${newMode}`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setMessages(parsedState.messages || []);
      setChats(parsedState.chats || []);
      setCurrentChatIndex(parsedState.currentChatIndex);
      setHasStartedChat(parsedState.hasStartedChat || false);
    } else {
      setMessages([]);
      setChats([]);
      setCurrentChatIndex(null);
      setHasStartedChat(false);
    }

    setMode(newMode);
  };

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const [isBankInformationOpen, setIsBankInformationOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isPWA] = useState(() => window.matchMedia('(display-mode: standalone)').matches);

  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    if (isPWA) {
      setIsSidebarOpen(false);
    }
  }, []);

  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const fullText = 'Angel';

  useEffect(() => {
    const animateText = () => {
      if (isTyping) return;
      let typingInterval: number | undefined;
      setIsTyping(true);
      setDisplayedText('');
      let currentIndex = 0;

      typingInterval = window.setInterval(() => {
        if (currentIndex < fullText.length) {
          setDisplayedText(fullText.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          window.clearInterval(typingInterval);
          setIsTyping(false);
        }
      }, 150);

      return () => {
        if (typingInterval) {
          window.clearInterval(typingInterval);
        }
        setIsTyping(false);
      };
    };

    animateText();
    const reanimateInterval = window.setInterval(animateText, 30000);

    return () => {
      window.clearInterval(reanimateInterval);
    };
  }, []);

  useEffect(() => {
    const stateToSave = {
      messages,
      chats,
      currentChatIndex,
      hasStartedChat
    };
    localStorage.setItem(`mode_state_${mode}`, JSON.stringify(stateToSave));
  }, [mode, messages, chats, currentChatIndex, hasStartedChat]);

  const generateChatTitle = (messages: Message[]): string => {
    const firstUserMessage = messages.find((m) => m.role === 'user');
    const modePrefix =
      mode === 'finance' ? '💰 ' : mode === 'education' ? '📚 ' : '';
    return (
      modePrefix +
      (firstUserMessage?.content.slice(0, 30) +
        (firstUserMessage?.content.length > 30 ? '...' : '') || 'New Chat')
    );
  };

  const saveCurrentChat = () => {
    if (messages.length > 0) {
      const updatedChats = [...chats];
      const chatTitle = generateChatTitle(messages);

      if (currentChatIndex !== null) {
        updatedChats[currentChatIndex] = { title: chatTitle, messages, mode: mode };
      } else {
        updatedChats.push({ title: chatTitle, messages, mode: mode });
      }
      setChats(updatedChats);
    }
  };

  const loadChat = (index: number) => {
    saveCurrentChat();
    setCurrentChatIndex(index);
    setMessages(chats[index].messages);
    setHasStartedChat(true);
  };

  const deleteChat = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedChats = chats.filter((_, i) => i !== index);
    setChats(updatedChats);

    if (currentChatIndex === index) {
      setMessages([]);
      setCurrentChatIndex(null);
      setHasStartedChat(false);
    } else if (currentChatIndex !== null && currentChatIndex > index) {
      setCurrentChatIndex(currentChatIndex - 1);
    }
  };

  const startNewChat = () => {
    saveCurrentChat();
    setMessages([]);
    setCurrentChatIndex(null);
    setHasStartedChat(false);
    setIsSidebarOpen(false);
  };

  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [forceProfileSetup, setForceProfileSetup] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Handle auth callback
    const handleAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        // Remove the access token from the URL
        window.history.replaceState({}, '', '/');
        
        // Show success message
        toast.success('Email verified successfully! Welcome to Angel.');
        
        // Refresh the page to ensure auth state is updated
        window.location.reload();
      }
    };

    handleAuthCallback();

    const storedUserId = localStorage.getItem('userId');
    setUserId(storedUserId);

    // Check if profile is complete
    const userProfile = localStorage.getItem('userProfile');
    if (!userProfile) {
      setForceProfileSetup(true);
      return;
    }

    const profile = JSON.parse(userProfile);
    const requiredFields = [
      profile.firstName,
      profile.lastName,
      profile.email
    ];

    const isComplete = requiredFields.every(field => field && field.trim() !== '');
    setIsProfileComplete(isComplete);
    if (!isComplete) {
      setForceProfileSetup(true);
    }
  }, []);

  useEffect(() => {
    const userProfile = localStorage.getItem('userProfile');
    if (!userProfile) {
      setForceProfileSetup(true);
      return;
    }

    const profile = JSON.parse(userProfile);
    const requiredFields = [
      profile.firstName,
      profile.lastName,
      profile.email,
      profile.phone,
      profile.dateOfBirth,
      profile.address.street,
      profile.address.city,
      profile.address.state,
      profile.address.zipCode,
      profile.address.country,
    ];

    const isComplete = requiredFields.every(
      (field) => field && field.trim() !== ''
    );
    setIsProfileComplete(isComplete);
    if (!isComplete) {
      setForceProfileSetup(true);
    }
  }, []);

  const handleProfileClose = useCallback(async () => {
    const userProfile = localStorage.getItem('userProfile');
    if (!userProfile) {
      toast.error('Please complete your profile to continue');
      return;
    }

    const profile = JSON.parse(userProfile);
    const requiredFields = [
      profile.firstName,
      profile.lastName,
      profile.email,
      profile.phone,
      profile.dateOfBirth,
      profile.address.street,
      profile.address.city,
      profile.address.state,
      profile.address.zipCode,
      profile.address.country,
    ];

    const isComplete = requiredFields.every(
      (field) => field && field.trim() !== ''
    );
    if (!isComplete) {
      return toast.error('Please fill out all required profile fields');
    }

    if (userId) {
      try {
        await syncUserProfile(userId, profile);
      } catch (error) {
        console.error('Error syncing profile:', error);
        toast.error('Failed to save profile. Please try again.');
        return;
      }
    }

    setIsProfileComplete(true);
    setForceProfileSetup(false);
    setIsProfileSettingsOpen(false);
    toast.success('Profile setup complete!');
  }, [userId]);

  return (
      <div className="min-h-[100dvh] h-[100dvh] bg-gradient-to-b from-blue-600 to-blue-600 flex flex-col overflow-hidden standalone:bg-blue-600">
        {messages.length === 0 && mode !== 'default' && (
          <div className="absolute top-0 left-0 right-0 bg-yellow-500/10 backdrop-blur-sm text-white/90 text-sm px-4 py-2 text-center">
            Beta Version: Information provided may not be 100% accurate. Please verify important financial decisions.
          </div>
        )}

        <ModeSelector onModeChange={handleModeChange} />

        {mode === 'direct-care' && (
          <Suspense fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
          }>
            <DirectCareMode />
          </Suspense>
        )}

        {mode !== 'direct-care' && (!hasStartedChat ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 pt-[env(safe-area-inset-top)] pb-[calc(4rem+env(safe-area-inset-bottom))] bg-gradient-to-b from-blue-600 to-blue-600">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-light text-white mb-3 animate-fade-in">
                {displayedText}
                <span className="animate-blink">|</span>
              </h1>
              <div className="absolute top-[max(env(safe-area-inset-top),1rem)] right-4 pt-4 flex items-center gap-4">
                <ProfileIcon
                  onProfileSettings={() => setIsProfileSettingsOpen(true)}
                  onViewLearning={() => setIsLearningOpen(true)}
                  onUpgradePremium={() => setIsUpgradeOpen(true)}
                  dbStatus={
                    isConnecting
                      ? 'connecting'
                      : dbConnected
                      ? 'connected'
                      : 'disconnected'
                  }
                />
              </div>
              {isConnecting && (
                <div className="absolute top-[max(env(safe-area-inset-top),1rem)] left-4 pt-4">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Connecting to database...</span>
                  </div>
                </div>
              )}
              {mode !== 'default' && (
                <div className="relative">
                  <p className="text-white/90 text-sm mb-8">
                    {mode === 'finance' && 'Your personal financial assistant (Beta)'}
                    {mode === 'education' && 'Your personal education assistant'}
                    {mode === 'direct-care' && 'Your personal direct care assistant'}
                  </p>
                  {mode === 'finance' && (
                    <div className="absolute -right-12 top-0 bg-blue-400/20 text-white/90 text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
                      Beta
                    </div>
                  )}
                </div>
              )}
              {chats.length > 0 && (
                <div className="mt-8 max-w-md w-full">
                  <h2 className="text-white text-xl font-light mb-4">
                    Recent Chats
                  </h2>
                  <div className="space-y-2">
                    {chats.map((chat, index) => (
                      <div key={index} className="flex items-center group">
                        <button
                          onClick={() => loadChat(index)}
                          className="flex-1 flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-xl transition-all"
                        >
                          {chat.mode === 'finance' ? (
                            <DollarSign className="h-5 w-5 opacity-70" />
                          ) : chat.mode === 'education' ? (
                            <GraduationCap className="h-5 w-5 opacity-70" />
                          ) : (
                            <MessageSquare className="h-5 w-5 opacity-70" />
                          )}
                          <span className="truncate">{chat.title}</span>
                        </button>
                        <button
                          onClick={(e) => deleteChat(index, e)}
                          className="ml-2 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          title="Delete chat"
                        >
                          <Trash2 className="h-5 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="w-full max-w-xl">
              <form className="relative">
                <input
                  type="text"
                  className="w-full h-[52px] px-5 text-[15px] bg-white/95 backdrop-blur-sm rounded-full border border-white/20 focus:border-white/30 focus:ring-0 shadow-lg hover:shadow-xl transition-all focus:outline-none"
                  placeholder={
                    mode === 'finance'
                      ? "Ask me anything about finance, investments, markets..."
                      : mode === 'education'
                      ? "Ask me about any K-12 subject - I'll help guide you to the answer..."
                      : "Ask me anything..."
                  }
                  onClick={() => setHasStartedChat(true)}
                />
              </form>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 relative h-[100dvh] overflow-hidden">
            {isSidebarOpen && (
              <div
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[140] sm:hidden"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            <Sidebar
              isOpen={isSidebarOpen}
              chats={chats}
              currentChatIndex={currentChatIndex}
              onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              onNewChat={startNewChat}
              onLoadChat={loadChat}
              onDeleteChat={deleteChat}
            />

            <div
              className={`flex-1 flex flex-col transition-all duration-300 overflow-hidden ${
                isSidebarOpen ? 'sm:ml-64' : ''
              }`}
            >
              <div className="bg-blue-700/90 backdrop-blur-sm py-4 px-4 md:px-6 flex items-center sticky top-0 z-[200] bg-clip-padding">
                <div className="w-full flex items-center justify-between pt-[env(safe-area-inset-top)]">
                  <div className="w-14">
                    <button
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      className="flex items-center gap-2 text-white/90 hover:text-white active:text-white/70 transition-colors touch-manipulation"
                    >
                      {isSidebarOpen ? (
                        <ChevronLeft className="h-6 w-6" />
                      ) : (
                        <ChevronRight className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <h1 className="text-2xl text-white">
                      {displayedText}
                      <span className="animate-blink">|</span>
                    </h1>
                  </div>
                  <div className="w-14 flex justify-end">
                    <ProfileIcon
                      onProfileSettings={() => setIsProfileSettingsOpen(true)}
                      dbStatus={dbConnected ? 'connected' : 'disconnected'}
                    />
                  </div>
                </div>
              </div>

              <ChatComponent
                messages={messages}
                setMessages={setMessages}
                chats={chats}
                setChats={setChats}
                currentChatIndex={currentChatIndex}
                setCurrentChatIndex={setCurrentChatIndex}
                hasStartedChat={hasStartedChat}
                setHasStartedChat={setHasStartedChat}
                isSidebarOpen={isSidebarOpen}
                mode={mode as 'default' | 'finance' | 'education'}
              />
            </div>
          </div>
        ))}

        {messages.length === 0 && (
          (mode === 'finance' || mode === 'education') && (
            <div className="absolute top-0 left-0 right-0 bg-yellow-500/10 backdrop-blur-sm text-white/90 text-sm px-4 py-2 text-center">
              Beta Version: Information provided may not be 100% accurate. Please verify important financial decisions.
            </div>
          )
        )}

        <PWAFooter
          onTermsClick={() => setIsTermsOpen(true)}
          onPrivacyClick={() => setIsPrivacyOpen(true)}
          onAboutClick={() => setIsAboutOpen(true)}
          className={hasStartedChat ? 'hidden' : 'mt-auto'}
        />

        <Terms isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
        <Privacy isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
        <About isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
        <LearningProgress isOpen={isLearningOpen} onClose={() => setIsLearningOpen(false)} />
        <UpgradePremium isOpen={isUpgradeOpen} onClose={() => setIsUpgradeOpen(false)} />
        <ProfileSettings
          isOpen={isProfileSettingsOpen}
          onClose={() => setIsProfileSettingsOpen(false)}
          required={forceProfileSetup}
        />
        <Notifications
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
        />
        <BankInformation
          isOpen={isBankInformationOpen}
          onClose={() => setIsBankInformationOpen(false)}
        />
      </div>
  );
}